/*
This file is located in the main branch
Last time the file was updated (UTC): [26.07.2024 9:54:27]
Permissions to update outside the main branch: [false]
*/

import { Button, Input, Pending, Tab, toInt } from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getProps } from 'axios/propApi';
import { getRolesByLevel } from 'axios/roleApi';
import useApi from 'hooks/useApi';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { RoleType } from 'models/api/roles';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, TabContent } from 'reactstrap';
import { RoleChangeModalFromType, RoleModalFromType } from './RoleChangeModal';
import RoleChangeModalTabAdditionalFeatures from './RoleChangeModalTabAdditionalFeatures';
import RoleChangeModalTabInfo from './RoleChangeModalTabInfo';
import RoleChangeModalTabRoles from './RoleChangeModalTabRoles';
import { useRoleWorkplaceState } from './Roles';

const RoleChangeModalForm = ({
    currentActiveTab,
    toggle,
    toggleTab,
    roleTypes,
    formHasRole,
    roleTypesIsFetching,
}: {
    currentActiveTab: string;
    toggle: (toggle: boolean) => void;
    toggleTab: (tab: string) => void;
    roleTypes: RoleType[];
    formHasRole: () => boolean;
    roleTypesIsFetching: boolean;
}) => {
    const { t } = useTranslation();
    const { watch, setValue } = useFormContext<RoleChangeModalFromType>();
    const roles = watch('roles');
    const typeId = toInt(watch('typeId'));
    const name = watch('name');
    const [showInactive, setShowInactive] = useState(true);
    const { selectedItem, handleError } = useRoleWorkplaceState();
    const api = useApi();

    const level = useMemo(() => {
        const level = roleTypes.find((r) => r.id === typeId)?.level;
        return level !== undefined ? level + 1 : -1;
    }, [typeId, roleTypes]);

    const { isFetching } = useQuery(['allRoles', level], () => getRolesByLevel(api, level), {
        onSuccess: (data) => {
            const roles = data.data.map<RoleModalFromType>((rr) => {
                const isEnable = !!selectedItem?.roles?.find(
                    (curentRole) => curentRole.id === rr.id
                );

                return {
                    ...rr,
                    enabled: isEnable,
                };
            });

            setValue('roles', roles);
        },
        onError: (e: AxiosError<AxiosErrorResponse>) => handleError(e.response?.data),
        enabled: !roleTypesIsFetching,
    });

    const { isFetching: propsLoading } = useQuery(
        ['RolesProps', selectedItem],
        () => getProps(api, 'Role', selectedItem?.id),
        {
            onSuccess: (props) => {
                setValue(
                    'props',
                    props.map((x) => ({ ...x, disabled: !formHasRole() }))
                );
            },
        }
    );

    return (
        <>
            <ModalHeader toggle={() => toggle(false)}>
                {name ? `${t('BackOfficeRole')} ${name}` : t('BackOfficeNew')}
            </ModalHeader>

            <ModalBody className="dictionary__modal-body custom-scrollbar">
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            onClick={() => toggleTab('1')}
                            href="#"
                            active={currentActiveTab === '1'}
                        >
                            <Trans i18nKey="BackOfficeInfo" />
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            onClick={() => toggleTab('2')}
                            href="#"
                            active={currentActiveTab === '2'}
                        >
                            <Trans i18nKey="BackOfficeRoles" />
                        </NavLink>
                    </NavItem>
                    {typeId === 0 && (
                        <NavItem>
                            <NavLink
                                onClick={() => toggleTab('3')}
                                href="#"
                                active={currentActiveTab === '3'}
                            >
                                <Trans i18nKey="BackOfficeAdditionalFeatures" />
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>

                <TabContent activeTab={currentActiveTab}>
                    <Tab
                        toggle={(tab) => toggleTab(tab?.toString() ?? '')}
                        tabId="1"
                        className="pt-3"
                    >
                        <RoleChangeModalTabInfo formHasRole={formHasRole} roleTypes={roleTypes} />
                    </Tab>
                    <Tab
                        toggle={(tab) => toggleTab(tab?.toString() ?? '')}
                        tabId="2"
                        className="pt-3"
                    >
                        {roleTypes.length ? (
                            <>
                                {selectedItem && (
                                    <>
                                        <Input
                                            type="checkbox"
                                            label={t('BackOfficeShowInactive')}
                                            checked={showInactive}
                                            id={'checkbox-show-inactive'}
                                            onChange={() => setShowInactive((i) => !i)}
                                        />
                                        <hr />
                                    </>
                                )}
                                <RoleChangeModalTabRoles
                                    formHasRole={formHasRole}
                                    showOnlyAssigned={!showInactive}
                                    allRoles={roles}
                                    isFetching={isFetching}
                                />
                            </>
                        ) : (
                            <Pending />
                        )}
                    </Tab>
                    {typeId === 0 && (
                        <Tab
                            toggle={(tab) => toggleTab(tab?.toString() ?? '')}
                            tabId="3"
                            className="pt-3"
                        >
                            {propsLoading ? <Pending /> : <RoleChangeModalTabAdditionalFeatures />}
                        </Tab>
                    )}
                </TabContent>
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" onClick={() => toggle(false)}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
                <Button
                    type="submit"
                    color="success"
                    disabled={!formHasRole() || !roleTypes.length || propsLoading}
                >
                    <Trans i18nKey="BackOfficeSave" />
                </Button>
            </ModalFooter>
        </>
    );
};

export default RoleChangeModalForm;
